import { useEffect, useState } from "react";
import { Download, FileText, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { BriefEmailStatus } from "@/components/BriefEmailStatus";
import { useAuth } from "@/context/AuthContext";

const RGB = "var(--glow-admin)";
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

export default function AdminPartnerBrief() {
  const { session } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/admin/partner-brief").then(({ data }) => setM(data)).catch((x) => setErr(errMsg(x))); }, []);
  if (session?.user?.platform_role !== "super_admin") return <p className="text-q-muted" data-testid="partner-brief-forbidden">Only the super admin can open the partner brief.</p>;
  if (err) return <p className="text-red-300" role="alert" data-testid="partner-brief-error">{err}</p>;
  if (!m) return <Loader />;
  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (x) { toast.error(errMsg(x)); api.get("/admin/partner-brief").then(({ data }) => setM(data)); } finally { setBusy(null); }
  };
  const regen = () => run("regen", async () => { setM((await api.post("/admin/partner-brief/regenerate")).data); toast.success("Partner brief regenerated"); });
  const email = () => run("email", async () => { const { data } = await api.post("/admin/partner-brief/email"); setM(data); toast.success(data.email?.message || "Sent"); });
  const download = () => run("dl", async () => {
    const { data } = await api.get("/admin/partner-brief/download", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "The-Queue-Partner-Brief.pdf"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  return (
    <div data-testid="partner-brief-page">
      <PageHeader eyebrow="Admin" title="Partner brief (PDF)" rgb={RGB} testid="partner-brief-title" />
      <section className="q-card p-5">
        <div className="flex items-start gap-4">
          <FileText className="mt-1 h-8 w-8 shrink-0 text-violet-300" />
          <div className="min-w-0">
            <p className="font-semibold text-q-text">The Queue – Partner Brief</p>
            <p className="text-sm text-q-muted">Product overview, the line-by-line feature catalogue, market price comparison and the subscription pricing recommendation.</p>
            <p className="mt-2 text-sm text-q-faint" data-testid="partner-brief-meta">
              {m.ready ? `Generated ${when(m.generated_at)} by ${m.generated_by} · ${m.pages} pages · ${Math.round(m.size / 1024)} KB` : "Not generated yet."}
            </p>
            <BriefEmailStatus e={m.email} onDownload={download} prefix="partner-brief" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={download} disabled={!m.ready || !!busy} data-testid="partner-brief-download-btn">
            {busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
          </button>
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={regen} disabled={!!busy} data-testid="partner-brief-regenerate-btn">
            {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate
          </button>
          <button className="q-btn q-btn-ghost min-h-[44px]" onClick={email} disabled={!m.ready || !!busy || !m.recipient} data-testid="partner-brief-email-btn">
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email to {m.recipient || "—"}
          </button>
        </div>
      </section>
    </div>
  );
}
